// components/legal/PrivacyPolicy.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RequestASongLogo } from '../components/common/RequestASongLogo';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <RequestASongLogo className="h-8 w-auto" />
            <button
              onClick={() => navigate(-1)}
              className="text-purple-600 hover:text-purple-800 font-medium"
            >
              ← Back
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Privacy Policy
            </h1>
            <p className="text-gray-600">
              Last Updated: {new Date().toLocaleDateString()}
            </p>
            
            {/* Key Points Summary */}
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-green-900 mb-3">
                🔒 Key Privacy Points
              </h2>
              <ul className="text-green-800 space-y-1 text-sm">
                <li>• We only collect information necessary to operate our Platform</li>
                <li>• We never sell your personal information to third parties</li>
                <li>• You control how your information is used</li>
                <li>• We use industry-standard security measures to protect your data</li>
                <li>• You can access, update, or delete your information at any time</li>
              </ul>
            </div>
          </div>

          {/* Privacy Content */}
          <div className="prose prose-lg max-w-none">
            <PrivacyContent />
          </div>

          {/* Your Rights Section */}
          <div className="mt-12 bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">
              Your Privacy Rights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">You can:</h4>
                <ul className="text-blue-700 space-y-1">
                  <li>• Access your personal information</li>
                  <li>• Correct inaccurate information</li>
                  <li>• Request deletion of your data</li>
                  <li>• Export your data</li>
                  <li>• Withdraw consent</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">Contact for privacy requests:</h4>
                <p className="text-blue-700">
                  Email: <a href="mailto:privacy@requestasong.com" className="underline">privacy@requestasong.com</a><br/>
                  Response time: Within 30 days<br/>
                  Include: Your account email and specific request
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Privacy Questions?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <p><strong>Company:</strong> Jackass Hospitality Group Pty Ltd</p>
                <p><strong>ACN:</strong> 652 087 520</p>
                <p><strong>Location:</strong> Canberra, ACT, Australia</p>
              </div>
              <div>
                <p><strong>Privacy Email:</strong> privacy@requestasong.com</p>
                <p><strong>Data Protection Officer:</strong> dpo@requestasong.com</p>
                <p><strong>General Support:</strong> support@requestasong.com</p>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                <strong>Australian Privacy Rights:</strong> If we haven't adequately addressed your privacy concerns, 
                you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) 
                at www.oaic.gov.au or 1300 363 992.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const PrivacyContent = () => (
  <div>
    <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introduction</h2>
    <p className="mb-6">
      Request a Song is operated by Jackass Hospitality Group Pty Ltd (ACN 652 087 520), located in Canberra, Australia. 
      This Privacy Policy describes how we collect, use, and protect your personal information when you use our platform 
      and services ("Service"). We are committed to protecting your privacy and ensuring transparency about our data practices.
    </p>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Information We Collect</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">2.1 Information You Provide Directly</h3>
    <div className="mb-6">
      <p className="mb-3"><strong>For Musicians:</strong></p>
      <ul className="list-disc ml-6 mb-4 space-y-1">
        <li><strong>Account Information:</strong> Display name, email address, phone number for SMS notifications</li>
        <li><strong>Profile Information:</strong> Performance style, venue preferences, song repertoire</li>
        <li><strong>Financial Information:</strong> Digital wallet details for payment processing</li>
        <li><strong>Performance Data:</strong> Songs performed, audience ratings, earnings history</li>
        <li><strong>Communications:</strong> Messages with venue hosts and audience members</li>
      </ul>
      
      <p className="mb-3"><strong>For Audience Members:</strong></p>
      <ul className="list-disc ml-6 mb-4 space-y-1">
        <li><strong>Request Information:</strong> Name, song requests, special messages to musicians</li>
        <li><strong>Payment Information:</strong> Payment authorization details (processed securely by third parties)</li>
        <li><strong>Ratings:</strong> Performance ratings and feedback</li>
        <li><strong>Event Data:</strong> Venue check-ins and request history</li>
      </ul>
    </div>

    <h3 className="text-xl font-medium text-gray-700 mb-3">2.2 Information Collected Automatically</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Usage Data:</strong> Platform interactions, feature usage, session duration</li>
      <li><strong>Device Information:</strong> Device type, operating system, browser type and version</li>
      <li><strong>Location Data:</strong> General location for venue matching (with permission)</li>
      <li><strong>Log Data:</strong> IP addresses, access times, referring URLs</li>
      <li><strong>Firebase Analytics:</strong> App performance and usage statistics</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">2.3 Information from Third Parties</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Payment Processors:</strong> Transaction confirmations and payment status</li>
      <li><strong>SMS Providers:</strong> Delivery confirmations for notification messages</li>
      <li><strong>Venue Partners:</strong> Event schedules and venue information</li>
      <li><strong>Analytics Providers:</strong> Aggregated usage statistics and performance metrics</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How We Use Your Information</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">3.1 Platform Operations</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Song Requests:</strong> Connect audience members with musicians, process requests</li>
      <li><strong>Payment Processing:</strong> Handle payment authorizations, process earnings for musicians</li>
      <li><strong>Performance Tracking:</strong> Monitor ratings, calculate musician earnings</li>
      <li><strong>SMS Notifications:</strong> Send real-time alerts for new requests and updates</li>
      <li><strong>Quality Assurance:</strong> Monitor service quality and resolve disputes</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">3.2 Service Improvement</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Analytics:</strong> Understand usage patterns to enhance user experience</li>
      <li><strong>Customer Support:</strong> Provide technical assistance and resolve issues</li>
      <li><strong>Security:</strong> Detect and prevent fraud, abuse, and security threats</li>
      <li><strong>Platform Development:</strong> Develop new features and improve existing functionality</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">3.3 Legal Compliance</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Australian Privacy Laws:</strong> Comply with Privacy Act 1988 and Australian Privacy Principles</li>
      <li><strong>Financial Regulations:</strong> Meet payment processing and tax reporting requirements</li>
      <li><strong>Venue Compliance:</strong> Support venue licensing and music industry requirements</li>
      <li><strong>International Users:</strong> Comply with GDPR and other applicable privacy laws</li>
    </ul>

    <h2 className="text-2xl font-semibent text-gray-800 mb-4">4. How We Share Your Information</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">4.1 We DO NOT Sell Your Information</h3>
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <p className="text-red-800 font-medium">
        We never sell, rent, or trade your personal information to third parties for marketing purposes.
      </p>
    </div>

    <h3 className="text-xl font-medium text-gray-700 mb-3">4.2 Platform Functionality</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Musicians and Audience:</strong> Song requests, requester names, and performance ratings</li>
      <li><strong>Venue Partners:</strong> Aggregate performance data and platform usage statistics</li>
      <li><strong>Payment Processing:</strong> Secure transmission to payment processors for transactions</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">4.3 Service Providers</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Firebase/Google Cloud:</strong> Data storage and app infrastructure</li>
      <li><strong>SMS Providers:</strong> Delivery of notification messages</li>
      <li><strong>Payment Processors:</strong> Secure payment authorization and processing</li>
      <li><strong>Analytics:</strong> Google Analytics for usage insights (anonymized data)</li>
      <li><strong>Customer Support:</strong> Tools to provide user assistance</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Data Storage and Security</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">5.1 Data Storage</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Primary Storage:</strong> Firebase servers (Google Cloud Platform infrastructure)</li>
      <li><strong>Geographic Location:</strong> Data may be stored in Google data centers globally</li>
      <li><strong>Backup Systems:</strong> Secure backups maintained for service continuity</li>
      <li><strong>Retention Period:</strong> Account data retained while account is active</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">5.2 Security Measures</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Encryption:</strong> Data encrypted in transit (HTTPS) and at rest</li>
      <li><strong>Access Controls:</strong> Limited access to authorized personnel only</li>
      <li><strong>Firebase Security:</strong> Google's enterprise-grade security infrastructure</li>
      <li><strong>Payment Security:</strong> PCI DSS compliant payment processing</li>
      <li><strong>Regular Audits:</strong> Security assessments and monitoring</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">5.3 Data Retention</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Active Accounts:</strong> Data retained while account remains active</li>
      <li><strong>Deleted Accounts:</strong> Personal data deleted within 30 days of account deletion</li>
      <li><strong>Financial Records:</strong> Payment data retained for 7 years for tax/audit purposes</li>
      <li><strong>Analytics Data:</strong> Anonymized usage data may be retained indefinitely</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Your Privacy Rights (Australian Privacy Principles)</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">6.1 Access and Correction Rights</h3>
    <p className="mb-3">Under Australian privacy law, you have the right to:</p>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Access:</strong> Request access to your personal information we hold</li>
      <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
      <li><strong>Deletion:</strong> Request deletion of your personal information (with some exceptions)</li>
      <li><strong>Complaint:</strong> Lodge a privacy complaint with us or the OAIC</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">6.2 International Users - Additional Rights</h3>
    <p className="mb-3">For users in the EU (GDPR) and California (CCPA):</p>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Data Portability:</strong> Receive your data in a machine-readable format</li>
      <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
      <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing communications</li>
      <li><strong>Restrict Processing:</strong> Request limitation of how we process your data</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">6.3 Exercising Your Rights</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Contact:</strong> Email privacy@requestasong.com with your request</li>
      <li><strong>Verification:</strong> We may need to verify your identity for security</li>
      <li><strong>Response Time:</strong> We respond within 30 days (Australian law requirement)</li>
      <li><strong>No Fee:</strong> Most requests are processed at no charge</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Cookies and Tracking</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">7.1 Types of Cookies</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Essential:</strong> Required for platform functionality and security</li>
      <li><strong>Analytics:</strong> Google Analytics to understand platform usage</li>
      <li><strong>Performance:</strong> Firebase Performance Monitoring for app optimization</li>
      <li><strong>Preferences:</strong> Remember your settings and preferences</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">7.2 Managing Cookies</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Browser Settings:</strong> Configure cookie preferences in your browser</li>
      <li><strong>Analytics Opt-Out:</strong> Use Google Analytics opt-out browser add-on</li>
      <li><strong>Impact:</strong> Disabling cookies may affect platform functionality</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. International Data Transfers</h2>
    <p className="mb-4">
      As an Australian company using global cloud infrastructure (Firebase/Google Cloud), 
      your data may be transferred to and stored in countries outside Australia, including the United States. 
      We ensure appropriate safeguards are in place for these transfers through:
    </p>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Google's Global Infrastructure:</strong> Enterprise-grade security and compliance</li>
      <li><strong>Standard Contractual Clauses:</strong> EU-approved transfer mechanisms</li>
      <li><strong>Adequacy Decisions:</strong> Transfers to countries with adequate protection levels</li>
      <li><strong>Australian Privacy Act Compliance:</strong> All transfers comply with APP 8</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Children's Privacy</h2>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Our platform is not intended for children under 13 years of age</li>
      <li>We do not knowingly collect personal information from children under 13</li>
      <li>If we become aware of such collection, we will delete the information promptly</li>
      <li>Parents may contact us to review or delete their child's information</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Changes to This Privacy Policy</h2>
    <p className="mb-4">
      We may update this Privacy Policy to reflect changes in our practices or applicable law. 
      We will notify you of material changes by:
    </p>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Email Notification:</strong> For significant changes affecting your rights</li>
      <li><strong>Platform Notifications:</strong> In-app notifications when you next use the service</li>
      <li><strong>Website Update:</strong> Updated policy posted on our website</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Complaints and Contact</h2>
    <p className="mb-4">
      If you have concerns about how we handle your personal information, please contact us first. 
      We are committed to resolving privacy concerns promptly and fairly.
    </p>
    
    <div className="bg-gray-100 rounded-lg p-4 mb-6">
      <p className="mb-2"><strong>Privacy Contact:</strong></p>
      <ul className="text-sm space-y-1">
        <li><strong>Email:</strong> privacy@requestasong.com</li>
        <li><strong>Response Time:</strong> Within 30 days</li>
        <li><strong>Include:</strong> Your account email, specific concern, and desired resolution</li>
      </ul>
    </div>

    <p className="text-sm text-gray-600">
      If you're not satisfied with our response, you may lodge a complaint with the Office of the 
      Australian Information Commissioner (OAIC) at www.oaic.gov.au or call 1300 363 992.
    </p>
  </div>
);

export default PrivacyPolicy;